// F1 App-Shell (TES-948): Mobile slide-in NavDrawer (screen-06).
// Linker slide-in (-translate-x-full -> translate-x-0, cubic-bezier(.4,0,.2,1))
// mit abgedunkeltem Backdrop (shadcn Sheet). Vollstaendige Rollen-Navigation.
// Sektionen via useNavSections (geteilt mit SideNav), Links via NavLinkBase.
// Verlustfrei: alle 5 Rollen-Bloecke, Offline-Guards, GuestMode, Sign-Out.
import { Fragment } from "react";
import { Link, useLocation } from "react-router";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { BookOpen, LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { AuthenticatedImage } from "@/components/ui/AuthenticatedImage";
import { ActiveTeamSelector } from "@/components/team/ActiveTeamSelector";
import type { ShellModel } from "./useShellModel";
import { hasOperativeRole, sidebarRoleBadgeVariants } from "./navConfig";
import { NavLinkBase } from "./NavLinkBase";
import { SideNavBrand } from "./SideNavBrand";
import { useNavSections, navSectionKey, type NavSection } from "./useNavSections";

interface NavDrawerProps {
  model: ShellModel;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNavigate: () => void;
}

const ACTIVE_LINK =
  "border-l-4 border-secondary active-nav-glow bg-sidebar-accent text-sidebar-accent-foreground";
const INACTIVE_LINK =
  "hover:bg-sidebar-accent text-sidebar-foreground/80 hover:text-sidebar-foreground";

// Eine Rollen-Sektion im Drawer: optionales Label + Items (via NavLinkBase).
// TES-969: operative Haupt-Nav rendert die Count-Pills (withBadge) — gleiche
// Daten-/Farb-/Pulse-Logik wie die Desktop-SideNav (Mobile-Paritaet, D7).
function DrawerSection({
  section,
  model,
  onNavigate,
}: {
  section: NavSection;
  model: ShellModel;
  onNavigate: () => void;
}) {
  return (
    <>
      {section.labelKey && (
        <div className="pt-4 pb-2 px-3">
          <span className="text-xs font-semibold text-sidebar-foreground/70 uppercase tracking-wider">
            {model.t(section.labelKey)}
          </span>
        </div>
      )}
      {section.items.map((item) => (
        <NavLinkBase
          key={item.nameKey}
          item={item}
          model={model}
          variant="drawer"
          offlineRespectsItem={section.isOperative}
          withBadge={section.isOperative}
          onNavigate={onNavigate}
        />
      ))}
    </>
  );
}

// HILFE-Sektion mit Benutzeranleitung im Drawer — eigene Sektion (Label „HILFE"); die
// Sichtbarkeit begrenzt der Aufrufer auf operative Rollen (TES-1683).
function DrawerGuideLink({ model, onNavigate }: { model: ShellModel; onNavigate: () => void }) {
  const location = useLocation();
  const linkClass = location.pathname === "/guide" ? ACTIVE_LINK : INACTIVE_LINK;
  return (
    <>
      <div className="pt-4 pb-2 px-3">
        <span className="text-xs font-semibold text-sidebar-foreground/70 uppercase tracking-wider">
          {model.t("nav.help")}
        </span>
      </div>
      <div className="px-3">
        <Link
          to="/guide"
          onClick={onNavigate}
          className={cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
            linkClass,
          )}
        >
          <BookOpen className="h-5 w-5 flex-shrink-0" />
          <span className="font-medium">{model.t("nav.userGuide")}</span>
        </Link>
      </div>
    </>
  );
}

// Profil-Link mit Avatar + Rollen-Badge.
function DrawerProfileLink({ model, onNavigate }: { model: ShellModel; onNavigate: () => void }) {
  const { t, avatarProxyUrl, profileUpdatedAt, displayName, initials, primaryRole } = model;
  return (
    <Link
      to="/profile"
      onClick={onNavigate}
      className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-sidebar-accent text-sidebar-foreground/80 hover:text-sidebar-foreground"
    >
      <Avatar className="h-8 w-8 flex-shrink-0 rounded-full overflow-hidden">
        {avatarProxyUrl ? (
          <AuthenticatedImage
            src={avatarProxyUrl}
            alt={displayName}
            className="h-8 w-8 min-w-8 min-h-8 object-cover"
            showSkeleton={false}
            showErrorUI={false}
            cacheBuster={profileUpdatedAt}
          />
        ) : null}
        <AvatarFallback className="bg-sidebar-primary text-sidebar-primary-foreground text-sm">
          {initials}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{displayName}</p>
        <Badge
          variant="outline"
          className={cn(
            "mt-0.5 text-[10px] h-4 px-1.5 font-normal border",
            sidebarRoleBadgeVariants[primaryRole],
          )}
        >
          {t(`roles.${primaryRole}`)}
        </Badge>
      </div>
    </Link>
  );
}

export function NavDrawer({ model, open, onOpenChange, onNavigate }: NavDrawerProps) {
  const { t, isSuperAdmin, isTenantContext, signOut, roles } = model;
  const sections = useNavSections(model);
  const showTeamSelector = !isSuperAdmin && !isTenantContext;
  // HILFE/Benutzeranleitung für operative Rollen, Super-Admin und rollenlose Fail-safe-Shells; nicht beim reinen Admin (TES-1683).
  const showUserGuide =
    !isTenantContext && (isSuperAdmin || roles.length === 0 || hasOperativeRole(roles));
  const handleSignOut = () => {
    onNavigate();
    signOut();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="left"
        className="w-sidebar-width p-0 bg-sidebar text-sidebar-foreground [&>button]:hidden"
        aria-describedby={undefined}
      >
        {/* TES-1735 (N11): Radix Dialog/Sheet-Content braucht einen Title als
            accessible name — sr-only, Muster wie AssistantMobile (SheetPrimitive.Title,
            der Sheet-Wrapper exportiert keinen eigenen SheetTitle). */}
        <SheetPrimitive.Title className="sr-only">{t("nav.menu")}</SheetPrimitive.Title>
        <nav className="flex flex-col h-full overflow-y-auto p-3 space-y-1">
          {/* Logo area */}
          <div className="flex items-center gap-2 px-3 py-4 border-b border-sidebar-border mb-2">
            <SideNavBrand model={model} />
          </div>

          {/* Active Team Selector */}
          {showTeamSelector && <ActiveTeamSelector collapsed={false} />}

          {/* Rollen-Sektionen (geteilt mit SideNav) — TES-2061/TES-2008: navSectionKey liefert
              Index-Fallback statt festem "__operative", da mehrere label-lose Sektionen
              existieren können (Key-Kollision). Identisches Keying wie SideNav (EINE Quelle). */}
          {sections.map((section, idx) => (
            <Fragment key={navSectionKey(section, idx)}>
              <DrawerSection section={section} model={model} onNavigate={onNavigate} />
            </Fragment>
          ))}

          {/* HILFE (Benutzeranleitung) — nur operative Rollen */}
          {showUserGuide && <DrawerGuideLink model={model} onNavigate={onNavigate} />}

          {/* User section */}
          <div className="mt-auto pt-3 border-t border-sidebar-border">
            <DrawerProfileLink model={model} onNavigate={onNavigate} />
            <Button
              variant="ghost"
              className="w-full flex items-center gap-3 px-3 py-2.5 h-auto rounded-lg text-destructive hover:bg-destructive/10"
              onClick={handleSignOut}
            >
              <LogOut className="h-5 w-5 flex-shrink-0" />
              <span className="font-medium">{t("nav.logout")}</span>
            </Button>
          </div>
        </nav>
      </SheetContent>
    </Sheet>
  );
}
